import { MonoclineCategoryProps, MonoclineDataProps, useCommonComponents, useLoadingOrError } from "@me8eon/components";
import { AISession, AiSessionData, AiSessionFlatTc, AiSessionTc, MessageWithActionAndData } from "./messages.sessions";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useLanguage } from "@me8eon/language";
import { useTranslation } from "@me8eon/translation";
import { useTime, isDateWithinGivenDays } from "@me8eon/time";
import { normalizeLocaleForIntl } from "@me8eon/time";
import { reverse } from "@me8eon/arrays";

import { useTheme } from "@me8eon/themes";
import { Icon, ThemedIcon } from "@me8eon/icons";
import { EonUiTooltip } from "@eon-ui/eon-ui-components-react";
import "./sessions.and.messages.scss";
import { useAiSessionIdOps, useAsyncSessionAndMessages, useSessionAndMessageOps } from "./session.messages.store";
import { ParentAndChildrenWithIds } from "@me8eon/async_storage";
import { isDataAs } from "@me8eon/async";
import { useLocation, useNavigate } from "react-router";
import { useWindowProperties } from "me-at-eon/src/context/WindowContext";
import { useAuthenticatedFetch } from "@me8eon/new_ai_utils/src/hooks/useAuthenticatedFetch";
import { deleteConversation } from "@me8eon/new_ai_utils/src/api/conversations";
import { CONVERSATION_RUN_STATE_EVENT, CONVERSATION_UPDATED_EVENT, defaultChatConfig } from "@me8eon/new_ai_utils/src/config/chat.config";
import { useUserData } from "@me8eon/react_authentication";

export const SessionsHeader: FC<MonoclineCategoryProps<AISession>> = ({ category, index, selected, onClick, isMobile }) => {
    const { monoClineStyles } = useTheme();
    const { header, selectedHeader } = monoClineStyles;
    const style = selected ? selectedHeader : header;
    return <div data-testid={`session.header.${index}`} data-selected={Boolean(selected)} style={{ ...style, fontWeight: "bold", ...(isMobile ? { paddingLeft: "8px" } : { paddingLeft: "14px" }) }} onClick={() => onClick?.(category, index)}>{category}</div>;
};

/** new-ai lists chats flat, with no Today/Yesterday/date group headers. */
export const HiddenSessionsHeader: FC<MonoclineCategoryProps<AISession>> = () => null;

export const SessionsData: FC<MonoclineDataProps<AISession>> = ({ category, item, index, selected, onClick, isMobile }) => {
    const { monoClineStyles } = useTheme();
    const { item: itemStyle, selectedItem } = monoClineStyles;
    const style = selected ? selectedItem : itemStyle;
    const { deleteChild: deleteSession } = useSessionAndMessageOps();
    const authenticatedFetch = useAuthenticatedFetch();
    const translate = useTranslation();
    const [isDeleting, setIsDeleting] = useState(false);
    const [isConversationRunning, setIsConversationRunning] = useState(false);

    useEffect(() => {
        const handleRunState = (event: Event) => {
            setIsConversationRunning(Boolean((event as CustomEvent<{ isRunning: boolean }>).detail?.isRunning));
        };
        window.addEventListener(CONVERSATION_RUN_STATE_EVENT, handleRunState);
        return () => window.removeEventListener(CONVERSATION_RUN_STATE_EVENT, handleRunState);
    }, []);

    const selectMsg = () => {
        document.getElementById("sidebar-backdrop")?.click();
        onClick?.(item, index);
    }
    const isNewConversation = item.source === "new";
    // A response is still streaming for this (selected/active) chat — don't let it be deleted mid-run.
    const deleteDisabled = isDeleting || (Boolean(selected) && isConversationRunning);

    const deleteItem = async () => {
        if (deleteDisabled) return;
        setIsDeleting(true);
        try {
            if (!isNewConversation) {
                await deleteSession(item.id);
                return;
            }
            const threadId = item.threadId ?? item.id.replace(/^new:/, "");
            await deleteConversation(authenticatedFetch, defaultChatConfig.conversationsEndpoint, threadId);
            window.dispatchEvent(new CustomEvent(CONVERSATION_UPDATED_EVENT));
        } catch (error) {
            console.error("Failed to delete conversation", error);
        } finally {
            setIsDeleting(false);
        }
    }

    return (
        <div
            data-testid={`session.data.${category}.${index}`}
            data-selected={Boolean(selected)}
            style={{ ...style, fontWeight: Boolean(selected) ? "600" : "", ...(isNewConversation ? { height: "24px" } : {}) }}
            className="item-container"
        >
            <button
                style={{
                    width: "100%", border: "0", background: "transparent", textAlign: "left", wordBreak: "break-word",
                    ...(isMobile ? { padding: "4px 8px" } : { padding: "4px 16px" }),
                    // new-ai keeps each chat title on one line (cut off with "...") in a fixed 24px row
                    ...(isNewConversation ? { minWidth: 0, height: "100%", paddingTop: 0, paddingBottom: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } : {}),
                }}
                title={isNewConversation ? item.name : undefined}
                data-testid={`session.data.${category}.${index}-span`}
                onClick={selectMsg}
            > {item.name} </button>
            <button
                className={`item-delete ${isMobile ? "" : "item-delete-web"}`}
                data-testid={`delete-${category}-${index}`}
                onClick={deleteItem}
                disabled={deleteDisabled}
                title={translate("chatBot.sessions.deleteMessage")}
            >
                {isDeleting
                    ? <span className="item-delete-spinner" data-testid={`delete-${category}-${index}-spinner`} aria-hidden="true" />
                    : <Icon name="trash_bin_small" />}
            </button>
        </div>
    );
};

const NoChatComponent = ({ onClose }: { onClose?: () => void }) => {
    const translate = useTranslation();
    const { recentChatsLayout } = useTheme();
    const { noChatsContainer, noChatsMessage, newChat } = recentChatsLayout;
    return (
        <div data-testid="nochat" style={onClose ? noChatsContainer : { marginTop: "32px" }}>
            <div style={noChatsMessage}>
                <span>
                    <ThemedIcon name="clock_recent" />
                </span>
                <span>{translate("chatBot.sessions.noRecentChat")}</span>
            </div>
            {onClose && (<div style={newChat} onClick={onClose}>
                <span>
                    <ThemedIcon name="add" />
                </span>{" "}
                <span>{translate("chatBot.sessions.startNewChat")}</span>
            </div>)}
        </div>
    );
};

export type SessionsAndMessagesProps = {
    isOpen: boolean;
    onClose: () => void;
}

export const RecentChat = ({ onClose, externalSessions = [] }: { onClose?: () => void, externalSessions?: AISession[] }) => {
    const LoadingOrError = useLoadingOrError<ParentAndChildrenWithIds<string, AiSessionData, string, MessageWithActionAndData[]>>();
    const { recentChatsLayout } = useTheme();
    const [state] = useAsyncSessionAndMessages();
    const translate = useTranslation();
    const { featureFlagEnabled } = useUserData();
    const {
        recentChatsList,
    } = recentChatsLayout;
    return (
        <div style={{ ...recentChatsList, ...(onClose ? { border: '1px solid #E5E5EA' } : {}) }}>
            <LoadingOrError
                state={state}
                whatWeAreLoading={translate("chatBot.sessions.title")}
                rootId="sessions-and-messages"
            >
                {(data) => {
                    function hasSession() {
                        if (data.parent.sessions.length > 1) return true;
                        return !!(data.parent.sessions.length === 1 && data.parent.sessions[0].name);
                    }

                    const hasChatsToShow = featureFlagEnabled
                        ? externalSessions.length > 0
                        : hasSession();

                    return hasChatsToShow ? <InvertedMonoCline data={data.parent} onClose={onClose} externalSessions={externalSessions} /> : <NoChatComponent onClose={onClose} />;
                }}

            </LoadingOrError>
        </div>
    )
}

export function SessionsAndMessages({
    isOpen,
    onClose,
}: SessionsAndMessagesProps) {
    // const LoadingOrError = useLoadingOrError<ParentAndChildrenWithIds<string, AiSessionData, string, MessageWithActionAndData[]>>();
    const [state] = useAsyncSessionAndMessages();
    const { deleteChild } = useSessionAndMessageOps()
    // const { deleteAllSessions } = useSessionAndMessageOps();
    const translate = useTranslation();
    const { recentChatsLayout } = useTheme();
    const {
        recentChats,
        recentChatsHeading,
        recentChatsHeadingTitle,
        recentChatsHeadingClear,
        recentChatsFooter,
    } = recentChatsLayout;
    if (!isOpen) return <></>;
    function deleteAllSessions() {
        if (!isDataAs(state)) return;
        const ids = state?.data?.parent.sessions.map(session => session.id) ?? [];
        deleteChild(...ids)
    }
    return (
        <div data-testid="ai.sessions.container" style={recentChats}>
            <div style={recentChatsHeading}>
                <div style={recentChatsHeadingTitle}>
                    <ThemedIcon name="clock_recent" />
                    {translate("chatBot.sessions.title")}
                </div>
                <div style={recentChatsHeadingClear}>
                    <span data-testid='delete.all.sessions' style={recentChatsHeadingClear} onClick={deleteAllSessions}>
                        <Icon name="trash_bin_small" />
                        {translate("chatBot.sessions.clearAll")}
                    </span>
                    <span data-testid='sessions.and.messages.close' onClick={onClose}>
                        <Icon name="close" />
                    </span>
                </div>
            </div>
            <RecentChat onClose={onClose} />
        </div>
    );
}

export function InvertedMonoCline({ data, onClose, externalSessions = [] }: { data: AiSessionData, onClose?: () => void, externalSessions?: AISession[] }) {
    const { MonoCline } = useCommonComponents();
    // const  sessionIdOps  = useSessionAndMessages();
    const navigate = useNavigate();
    const location = useLocation();
    const translate = useTranslation();
    const [sessionId, setSessionId] = useAiSessionIdOps();
    const { recentChatsLayout } = useTheme();
    const {
        recentChatsFooter,
    } = recentChatsLayout;
    const timeService = useTime();
    const [language] = useLanguage();
    const relativeDayLabels = useMemo(() => {
        const formatter = new Intl.RelativeTimeFormat(normalizeLocaleForIntl(language), { numeric: "auto" });
        return {
            today: formatter.format(0, "day"),
            yesterday: formatter.format(-1, "day"),
        };
    }, [language]);
    const { featureFlagEnabled } = useUserData();
    const { screenWidth } = useWindowProperties();
    const isMobile = screenWidth < 768;
    const items = useMemo(() => {
        if (featureFlagEnabled) {
            return externalSessions
                .filter(session => session.name && isDateWithinGivenDays(session.time, 30))
                .sort((a, b) => Date.parse(b.time) - Date.parse(a.time));
        }

        const legacySessions = reverse(
            data.sessions
                .filter(session => session.name && isDateWithinGivenDays(session.time, 30))
                .map(session => ({ ...session, source: "legacy" as const })),
        );
        return legacySessions
            .filter(session => session.name && isDateWithinGivenDays(session.time, 30))
            .sort((a, b) => Date.parse(b.time) - Date.parse(a.time));
    }, [data.sessions, externalSessions, featureFlagEnabled]);
    const activeThreadId = useMemo(() => {
        const newAiThread = location.pathname.match(/^\/new-ai\/([^/?#]+)/);
        // `/new-ai/shared` is a shared-chat view, not one of the user's threads.
        if (!newAiThread || newAiThread[1] === "shared") return undefined;
        return decodeURIComponent(newAiThread[1]);
    }, [location.pathname]);
    const selectedItem = useMemo(() => {
        if (activeThreadId) {
            return items.find(item => item.source === "new" && item.threadId === activeThreadId);
        }
        if (location.pathname.startsWith("/new-ai")) {
            return undefined;
        }
        return items.find(item => item.id === sessionId);
    }, [activeThreadId, items, location.pathname, sessionId]);
    const onItemClick = (item: AISession) => {
        if (item.source === "new") {
            const threadId = item.threadId ?? item.id.replace(/^new:/, "");
            navigate(threadId ? `/new-ai/${encodeURIComponent(threadId)}` : "/new-ai");
            onClose?.();
            return;
        }
        navigate("/ai");
        setSessionId(item.id);
        onClose?.();
    };

    // The "last 30 days will be stored" footer only applies to the legacy `/ai` chats, not `/new-ai`.
    const isNewAiPage = location.pathname.startsWith("/new-ai");
    const disclaimer = isNewAiPage ? undefined : <div>
        <span>{translate("chatBot.sessions.footer")} &nbsp;</span>
        <span>
            <a href="#" onClick={(e) => e.preventDefault()} style={recentChatsFooter}>{translate("chatBot.sessions.learnMore")}</a>
            <EonUiTooltip
                visibility="onClick"
                placement="top"
            >
                <p style={recentChatsFooter}>{translate("chatBot.sessions.learnMoreTooltip")}</p>
            </EonUiTooltip>
        </span>
    </div>

    return <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
        <MonoCline
            disclaimer={disclaimer}
            items={items}
            isMobile={isMobile}
            typeClass={featureFlagEnabled ? AiSessionFlatTc : AiSessionTc(timeService, language, relativeDayLabels)}
            Header={featureFlagEnabled ? HiddenSessionsHeader : SessionsHeader}
            Data={SessionsData}
            onDataClick={onItemClick}
            selectedItem={selectedItem}
        />
    </div>;

}
